import React, { useState } from "react";
import { Alert, Button, Empty, Modal, Skeleton, Table, Tabs, Tag, Tooltip, theme } from "antd";
import { ApartmentOutlined, BarChartOutlined, FileTextOutlined, HistoryOutlined,
  CalculatorOutlined, CodeSandboxOutlined, GoldOutlined, MinusOutlined, MoreOutlined,
  PlusOutlined, ShoppingCartOutlined, TagOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import useBusinessResource from "../hooks/useBusinessResource";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import ProductStatusBadge from "../components/ProductStatusBadge";
import "./product-details-modal.css";

const number = (value, digits = 4) => value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
  ? "—" : Number(value).toLocaleString("fr-FR", { maximumFractionDigits: digits });
const money = (value) => number(value, 0) === "—" ? "—" : `${number(value, 0)} CFA`;
const date = (value) => value && dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY HH:mm") : "Non renseignée";
const timestamp = (value) => value && dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY HH:mm:ss") : "Non renseignée";
const isVariantParent = (product) => Number(product?.is_variant_parent) === 1;
const accessLost = (resource) => [401, 403, 404].includes(resource.error?.response?.status);
const productTypes = {
  raw: { label: "Matière première", color: "gold" },
  production: { label: "Unité de production", color: "geekblue" },
  single: { label: "Produits finis", color: "green" },
};
const movementTypes = {
  sale: ["Vente", "red"], purchase: ["Achat", "green"], adjustment: ["Ajustement", "orange"],
  production: ["Production", "blue"], transfer: ["Transfert", "purple"], return: ["Retour", "cyan"],
};
const defaultTerminology = {
  unitsTitle: "Unités de production", unitSingularTitle: "Unité de production",
  unitSingular: "unité de production", unitPlural: "unités de production",
  materialDefiniteSingular: "cette matière première", configurationSingular: "configuration",
};

function listFrom(data, field) {
  const rows = Array.isArray(data) ? data : data?.[field];
  if (!Array.isArray(rows)) throw new Error("La liste reçue est indisponible.");
  return rows;
}

export function useProductDetailsStyle() {
  const { token } = theme.useToken();
  const dark = ["#000", "#000000"].includes(token.colorBgBase);
  return {
    "--pd-surface": token.colorBgElevated, "--pd-text": token.colorText,
    "--pd-heading": dark ? token.colorTextHeading : token.colorInfoTextActive,
    "--pd-secondary": token.colorTextSecondary, "--pd-border": token.colorBorder,
    "--pd-fill": token.colorFillAlter, "--pd-accent": dark ? token.colorTextHeading : token.colorPrimary,
    "--pd-error-text": dark ? token.colorText : token.colorErrorText,
    "--pd-error-border": dark ? token.colorErrorTextHover : token.colorErrorBorder,
  };
}

function ProductHeading({ product, warehouseName }) {
  const [failedImage, setFailedImage] = useState(null);
  const image = product?.image;
  const type = productTypes[String(product?.product_type || "").toLowerCase()];
  return <><div className="product-details__caption">Détails du produit</div><div className="product-details__heading">
    <div className="product-details__thumbnail">
      {image && image !== failedImage
        ? <img src={image} alt={product.name} onError={() => setFailedImage(image)} />
        : <CodeSandboxOutlined aria-label="Aucune image du produit" />}
    </div>
    <div className="product-details__identity">
      <div className="product-details__title-row">
        <h2>{product?.name || "Fiche produit"}</h2>
        {product && <div className="product-details__badges"><Tag color={type?.color || "default"}>{type?.label || product.product_type || "Type non renseigné"}</Tag>
          <ProductStatusBadge product={product} size="small" /></div>}
      </div>
      {product && <div className="product-details__subtitle">
        <span>{product.categorie_nom || "Sans catégorie"}</span>
        <span>{product.unit?.name || product.unit?.short_name || "Unité non renseignée"}
          {product.unit?.name && product.unit?.short_name ? ` (${product.unit.short_name})` : ""}</span>
      </div>}
      <div className="product-details__establishment">
        <span>Établissement · <strong>{warehouseName || "Non renseigné"}</strong></span>
      </div>
    </div>
  </div></>;
}

function ProductSummary({ product, quantity }) {
  const rate = product.tax_rate ?? product.taxe_rate;
  const withTax = number(product.sales_price) !== "—" && number(rate) !== "—"
    ? Number(product.sales_price) * (1 + Number(rate) / 100) : null;
  const stock = number(product.current_stock);
  return <section className="product-details__summary" aria-label="Stock et tarification">
      <div className="product-details__stock"><GoldOutlined aria-hidden="true" />
        <div><span>Stock actuel</span><div className="product-details__stock-value"><strong>{quantity(product.current_stock)}</strong>
          {stock !== "—" && Number(product.current_stock) <= 0 && <Tag color="error">
            {Number(product.current_stock) < 0 ? "Stock négatif" : "Stock nul"}</Tag>}</div>
          <span className="product-details__stock-hint">Niveau disponible immédiatement</span></div>
      </div>
      <div className="product-details__price"><TagOutlined aria-hidden="true" /><dl><dt>Prix d'achat</dt><dd>{money(product.purchase_price)}</dd></dl></div>
      <div className="product-details__price"><TagOutlined aria-hidden="true" /><dl><dt>Prix de vente</dt><dd>{money(product.sales_price)}</dd></dl></div>
      <div className="product-details__price"><CalculatorOutlined aria-hidden="true" /><dl><dt>Prix avec taxe</dt><dd>{money(withTax)}</dd></dl></div>
    </section>;
}

function ProductSheet({ product, quantity }) {
  const rate = product.tax_rate ?? product.taxe_rate;
  const type = productTypes[String(product.product_type || "").toLowerCase()];
  const unit = product.unit?.name ? `${product.unit.name}${product.unit.short_name ? ` (${product.unit.short_name})` : ""}`
    : product.unit?.short_name || "Non renseignée";
  const sections = [
    { title: "Identification", rows: [
      ["Type de produit", type?.label || product.product_type || "Non renseigné"],
      ["Catégorie", product.categorie_nom || "Sans catégorie"], ["Unité", unit],
      ["Code de l'article", product.item_code || "Non renseigné"],
      ["Code-barres", product.barcode_value || product.item_code || "Non renseigné"],
    ] },
    { title: "Stock et suivi", rows: [
      ["Stock d'ouverture", quantity(product.opening_stock)], ["Seuil d'alerte", quantity(product.stock_quantitiy_alert)],
      ["Taux d'imposition", number(rate) === "—" ? "Non renseigné" : Number(rate) ? `${number(rate)} %` : "Sans taxe"],
      ["Date de création", date(product.created_at)], ["Dernière modification", date(product.updated_at)],
    ] },
  ];
  return <><div className="product-details__sheet">{sections.map((section) => <section key={section.title} aria-label={section.title}>
    <h3>{section.title}</h3><dl className="product-details__facts">{section.rows.map(([label, value]) => <div key={label}>
      <dt>{label}</dt><dd>{value}</dd></div>)}</dl>
  </section>)}</div>{product.description && <p className="product-details__description">{product.description}</p>}</>;
}

function activityColumns(tab, product, quantity, variantLabels) {
  if (tab === "commandes") {
    const item = (order) => order.items?.find((line) => Number(line.product_id) === Number(product.id));
    return [
      { title: "Date de commande", dataIndex: "order_date", render: timestamp },
      { title: "Type de commande", dataIndex: "order_type", render: (type) => ({ purchase: "Achats", sales: "Ventes", "stock-transfer": "Transfert" }[type] || type) },
      { title: "Quantité", key: "quantity", render: (_, order) => quantity(item(order)?.quantity ?? order.total_quantity) },
      { title: "Prix unitaire", key: "price", align: "right", render: (_, order) => money(item(order)?.unit_price) },
      { title: "Remise", key: "discount", render: (_, order) => item(order) ? `${number(item(order).discount_rate ?? 0)} %` : "—" },
      { title: "Taxe", key: "tax", render: (_, order) => item(order) ? `${number(item(order).tax_rate ?? 0)} %` : "—" },
      { title: "Sous-total", key: "total", align: "right", render: (_, order) => money(item(order)?.subtotal) },
    ];
  }
  if (tab === "historique") return [
    { title: "Date", dataIndex: "created_at", render: timestamp },
    ...(isVariantParent(product) ? [{ title: "Variante", dataIndex: "variant_label", render: (value) => value ? <Tag color="blue">{value}</Tag> : "—" }] : []),
    { title: "Établissement", dataIndex: "warehouse_name", render: (value) => value || "—" },
    { title: "Quantité", dataIndex: "quantity", render: quantity },
    { title: "Type", dataIndex: "movement_type", render: (value) => <Tag color={movementTypes[value]?.[1] || "default"}>{movementTypes[value]?.[0] || value}</Tag> },
    { title: "Détails / Référence", key: "reference", render: (_, row) => <>
      {row.reference_type === "production_log" && row.production_unit_name && <strong className="product-details__table-title">{row.production_unit_name}</strong>}
      <span>{row.reference_type || "—"}{row.reference_id ? ` : ${row.reference_id}` : ""}</span></> },
    { title: "Lots", key: "lots", render: (_, row) => row.lot_details
      || String(row.remarks || "").match(/Lots consommes:\s*(.+)$/i)?.[1]
      || String(row.remarks || "").match(/\(Lot\s+([^)]+)\)/i)?.[1] || "—" },
    { title: "Mouvement", dataIndex: "quantity", key: "direction", render: (value) => Number(value) > 0
      ? <Tag color="green" icon={<PlusOutlined />}>Entrée</Tag> : Number(value) < 0
        ? <Tag color="red" icon={<MinusOutlined />}>Sortie</Tag> : <Tag>Aucun</Tag> },
    { title: "Remarques", dataIndex: "remarks", render: (value) => value || "—" },
  ];
  return [
    { title: "Date / Heure", dataIndex: "created_at", render: (value) => <Tooltip title={value && dayjs(value).format("DD/MM/YYYY HH:mm:ss")}>{date(value)}</Tooltip> },
    ...(isVariantParent(product) ? [{ title: "Variante", dataIndex: "resource_id", render: (value) => variantLabels?.[Number(value)]
      ? <Tag color="blue">{variantLabels[Number(value)]}</Tag> : Number(value) === Number(product.id) ? <Tag>Parent</Tag> : "—" }] : []),
    { title: "Utilisateur", dataIndex: "username", render: (value, row) => value || (row.user_id ? `ID : ${row.user_id} (supprimé)` : <Tag>Système</Tag>) },
    { title: "Action", dataIndex: "action_type", render: (value) => <Tag color="purple">{value}</Tag> },
    { title: "Statut", dataIndex: "status", render: (value) => <Tag color={{ success: "success", failure: "error", partial: "warning" }[value] || "default"}>{value}</Tag> },
    { title: "Méthode", dataIndex: "method", render: (value) => <Tag color={{ POST: "green", PUT: "orange", DELETE: "red" }[value] || "default"}>{value}</Tag> },
    { title: "Durée (ms)", dataIndex: "duration_ms", align: "right", render: (value) => number(value, 0) },
    { title: "IP", dataIndex: "ip_address", render: (value) => value || "—" },
  ];
}

function ProductionUnits({ product, quantity, terminology }) {
  const usages = Array.isArray(product.production_unit_usages) ? product.production_unit_usages : [];
  return <>
    <Alert type="info" showIcon className="product-details__notice" message={usages.length
      ? `${usages.length} ${usages.length === 1 ? terminology.unitSingular : terminology.unitPlural} ${usages.length === 1 ? "utilise" : "utilisent"} ${terminology.materialDefiniteSingular}`
      : `Aucun lien entre ${terminology.materialDefiniteSingular} et les ${terminology.unitPlural}.`}
      description={usages.length
        ? `La quantité affichée correspond à la consommation par défaut dans la ${terminology.configurationSingular} de base de chaque unité.`
        : `Aucune ${terminology.configurationSingular} ne référence actuellement ${terminology.materialDefiniteSingular} dans ce contexte.`} />
    <Table size="small" dataSource={usages} rowKey={(row) => row.id || `${row.name}-${row.item_code}`}
      scroll={{ x: 740 }} pagination={{ showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] }}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`Aucune ${terminology.unitSingular} liée`} /> }}
      columns={[
        { title: terminology.unitSingularTitle, key: "unit", render: (_, row) => <><strong className="product-details__table-title">{row.name || "—"}</strong><span>Code : {row.item_code || "—"}</span></> },
        { title: "Établissement", dataIndex: "warehouse_name", render: (value) => value || "—" },
        { title: "Qté par défaut consommée", dataIndex: "default_consumption_quantity", align: "right", render: quantity },
        { title: "Production de base", key: "output", align: "right", render: (_, row) => `${number(row.default_output_quantity)}${row.unit?.short_name ? ` ${row.unit.short_name}` : ""}` },
      ]} />
  </>;
}

function ProductDetailsSession({ productId, userId, companyId, warehouseId, warehouseName, onClose, terminology: terminologyProp }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const style = useProductDetailsStyle();
  const terminology = { ...defaultTerminology, ...terminologyProp };
  const scope = { userId, companyId, warehouseId, moduleScope: "commercial" };
  // Capture the requested company for every read; apiClient also supplies the bearer token.
  const options = (signal, params = {}) => ({ signal, params, headers: { "X-Company-Id": String(companyId) } });
  const productResource = useBusinessResource({ ...scope, resource: "product-detail", parameters: [productId],
    enabled: Boolean(productId), active: true,
    load: async (signal) => {
      const { data } = await apiClient.get(`${API_CONFIG.BASE_URL}/api/produits/${productId}`, options(signal, { warehouse_id: warehouseId }));
      if (!data || Number(data.id) !== Number(productId)) throw new Error("La fiche reçue est indisponible.");
      return data;
    },
  });
  const product = accessLost(productResource) ? null : productResource.data;
  const activityEnabled = Boolean(product && ["commandes", "historique", "audit"].includes(activeTab));
  const activity = useBusinessResource({ ...scope, resource: "product-activity",
    parameters: [productId, activeTab, ...(activeTab === "audit" ? [pagination.current, pagination.pageSize, isVariantParent(product)] : [])],
    enabled: activityEnabled, active: activityEnabled,
    load: async (signal) => {
      if (activeTab === "commandes") {
        const { data } = await apiClient.get(`${API_CONFIG.BASE_URL}/api/orders`, options(signal, { product_id: productId, warehouse: warehouseId, limit: 1000 }));
        return { rows: listFrom(data, "orders") };
      }
      if (activeTab === "historique") {
        const { data } = await apiClient.get(`${API_CONFIG.BASE_URL}/api/stock-history`, options(signal, { product_id: productId, warehouse_id: warehouseId, limit: 1000 }));
        return { rows: listFrom(data, "stock_history") };
      }
      const params = { resourceType: "product", module: "produits", limit: pagination.pageSize, offset: (pagination.current - 1) * pagination.pageSize };
      const variantLabels = {};
      if (isVariantParent(product)) {
        const { data } = await apiClient.get(`${API_CONFIG.BASE_URL}/api/produits/${productId}/variants`, options(signal));
        const variants = listFrom(data, "variants");
        params.resourceIds = [productId, ...variants.map((variant) => variant.id)].join(",");
        variants.forEach((variant) => { variantLabels[Number(variant.id)] = variant.variant_label; });
      } else params.resourceId = productId;
      const { data } = await apiClient.get(`${API_CONFIG.BASE_URL}/api/audit/logs`, options(signal, params));
      if (!data?.success) throw new Error("Le journal d’audit est indisponible.");
      return { rows: listFrom(data, "logs"), total: data.total, variantLabels };
    },
  });
  const quantity = (value) => number(value) === "—" ? "—" : `${number(value)}${product?.unit?.short_name ? ` ${product.unit.short_name}` : ""}`;
  const activityData = accessLost(activity) ? null : activity.data;
  const tabs = [
    { key: "overview", label: "Fiche produit", icon: <FileTextOutlined aria-hidden="true" /> },
    { key: "commandes", label: "Commandes", icon: <ShoppingCartOutlined aria-hidden="true" />, subtitle: "Commandes associées au produit dans cet établissement" },
    { key: "historique", label: "Historique des stocks", icon: <BarChartOutlined aria-hidden="true" />, subtitle: "Mouvements, références et lots du produit" },
    ...(String(product?.product_type).toLowerCase() === "raw" ? [{ key: "production_units", label: terminology.unitsTitle, icon: <ApartmentOutlined aria-hidden="true" />, subtitle: "Utilisation de cette matière première" }] : []),
    { key: "audit", label: "Journal d'audit", icon: <HistoryOutlined aria-hidden="true" />, subtitle: "Traçabilité des opérations sur le produit" },
  ];
  const resourceNotice = (resource, label) => <BusinessResourceNotice
    resource={accessLost(resource) ? { ...resource, data: undefined } : resource} label={label}
    errorMessage={resource.error?.response?.data?.error || resource.error?.response?.data?.message || `Impossible d’actualiser ${label}.`} />;
  return <Modal open centered width={1180} className="product-details-dialog" style={style}
    title={<ProductHeading product={product} warehouseName={warehouseName || product?.warehouse_nom} />}
    onCancel={onClose} footer={<Button type="primary" onClick={onClose}>Fermer</Button>}>
    {productResource.error && <div className="product-details__resource-notice">{resourceNotice(productResource, "la fiche du produit")}</div>}
    {!product && productResource.loading ? <div className="product-details__loading"><Skeleton active paragraph={{ rows: 10 }} /></div>
      : product ? <>
        {productResource.loading && <div className="product-details__refresh" role="status">Actualisation de la fiche du produit…</div>}
        {product.warehouse_id && Number(product.warehouse_id) !== Number(warehouseId) && <Alert showIcon type="info"
          className="product-details__transfer" message={`Produit transféré · Établissement propriétaire : ${product.warehouse_nom || "Non renseigné"}`} />}
        <ProductSummary product={product} quantity={quantity} />
        <Tabs className="product-details__tabs" destroyInactiveTabPane tabPosition="top"
          more={{ icon: <MoreOutlined aria-label="Autres rubriques" />, trigger: "click" }}
          activeKey={activeTab === "production_units" && String(product.product_type).toLowerCase() !== "raw" ? "overview" : activeTab}
          onChange={(tab) => { setActiveTab(tab); setPagination({ current: 1, pageSize: 10 }); }}
          items={tabs.map((tab) => ({ key: tab.key, label: <span className="product-details__nav-label">{tab.icon}<span>{tab.label}</span></span>,
            children: <div className="product-details__panel">{tab.key !== "overview" && <><h3>{tab.label}</h3><p className="product-details__intro">{tab.subtitle}</p></>}
              {tab.key === "overview" ? <ProductSheet product={product} quantity={quantity} />
                : tab.key === "production_units" ? <ProductionUnits product={product} quantity={quantity} terminology={terminology} />
                  : <>{(activity.error || (activity.loading && activityData)) && resourceNotice(activity, tab.label.toLowerCase())}
                    {activity.error && !activityData ? null : <Table size="small" loading={activity.loading}
                      columns={activityColumns(tab.key, product, quantity, activityData?.variantLabels)}
                      dataSource={activityData?.rows || []} scroll={{ x: tab.key === "historique" ? 1200 : 900 }}
                      rowKey={(row) => row.id || `${row.reference_type}-${row.reference_id}-${row.created_at}-${row.quantity}`}
                      pagination={{ ...pagination, total: tab.key === "audit" ? Number(activityData?.total || 0) : activityData?.rows?.length || 0,
                        showSizeChanger: true, pageSizeOptions: ["10", "20", "50"],
                        onChange: (current, pageSize) => setPagination({ current, pageSize }) }}
                      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={activity.loading ? "Chargement…"
                        : tab.key === "commandes" ? "Aucune commande pour ce produit dans cet établissement"
                          : tab.key === "historique" ? "Aucun mouvement de stock pour ce produit" : "Aucune opération dans le journal d'audit"} /> }} />}</>}
            </div> }))} />
      </> : !productResource.error && <Empty description="Sélectionnez une entreprise et un établissement pour consulter le produit." />}
  </Modal>;
}

export default function ProductDetailsModal({ open, canView = false, ...props }) {
  // Unmount on close or context change: reset tabs/pages and detach obsolete requests.
  return open && canView ? <ProductDetailsSession
    key={`${props.userId}:${props.companyId}:${props.warehouseId}:${props.productId}`} {...props} /> : null;
}
